import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PropTypes from "prop-types";

// Simple markdown renderer for changelog (supports headings, lists, and paragraphs)
function renderMarkdown(md) {
  //console.log("renderMarkdown input:", md);
  //console.log("renderMarkdown input type:", typeof md);
  //console.log("renderMarkdown input length:", md ? md.length : "undefined");

  // ตรวจสอบว่ามีข้อมูลหรือไม่
  if (!md || typeof md !== "string" || md.trim().length === 0) {
    //console.log("No valid markdown data provided");
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          ไม่พบข้อมูล Changelog
        </Typography>
      </Box>
    );
  }

  // Parse changelog รูปแบบของโปรเจกต์นี้ (## <small>version (date)</small>)
  const entries = [];
  const lines = md.split("\n");
  let currentEntry = null;
  let changes = [];

  //console.log("Processing lines:", lines.length);

  lines.forEach((line, index) => {
  //  //console.log(`Line ${index}: "${line}"`);

    // ข้ามบรรทัดว่างหรือบรรทัดที่ไม่จำเป็น
    if (line.trim() === "") {
      return;
    }

    // ตรวจหา version pattern: ## <small>version (date)</small>
    const versionMatch = line.match(
      /^##\s*<small>([^(]+)\(([^)]+)\)<\/small>$/
    );
    if (versionMatch) {
      //console.log(`Found version at line ${index}:`, versionMatch);
      // บันทึก entry ก่อนหน้า
      if (currentEntry && changes.length > 0) {
        currentEntry.changes = [...changes];
        entries.push(currentEntry);
      }

      // สร้าง entry ใหม่
      currentEntry = {
        version: versionMatch[1].trim(),
        date: versionMatch[2].trim(),
        changes: [],
      };
      changes = [];
      return;
    }

    // ตรวจหา change item pattern: * description ([commit](link))
    const changeMatch = line.match(
      /^\*\s*(.+?)\s*\(\[([a-f0-9]+)\]\([^)]+\)\)$/
    );
    if (changeMatch && currentEntry) {
      //console.log(`Found change at line ${index}:`, changeMatch);
      const description = changeMatch[1].trim();
      const commitHash = changeMatch[2].trim();

      // แยกประเภทการเปลี่ยนแปลงตามคำหลัก
      let type = "Changed";
      let icon = "🔄";
      let color = "#ff9800";

      if (
        description.includes("แก้") ||
        description.includes("fix") ||
        description.includes("Fix")
      ) {
        type = "Fixed";
        icon = "🔧";
        color = "#2196f3";
      } else if (
        description.includes("เพิ่ม") ||
        description.includes("add") ||
        description.includes("Add") ||
        description.includes("new") ||
        description.includes("New")
      ) {
        type = "Added";
        icon = "✨";
        color = "#4caf50";
      } else if (
        description.includes("ลบ") ||
        description.includes("remove") ||
        description.includes("Remove") ||
        description.includes("delete") ||
        description.includes("Delete")
      ) {
        type = "Removed";
        icon = "🗑️";
        color = "#f44336";
      }

      changes.push({
        type,
        icon,
        color,
        description,
        commitHash,
      });
      return;
    }
  });

  // เพิ่ม entry สุดท้าย
  if (currentEntry && changes.length > 0) {
    currentEntry.changes = [...changes];
    entries.push(currentEntry);
  }

  //console.log("Parsed entries:", entries);
  //console.log("Number of entries:", entries.length);

  // Sort by date desc (ใหม่ก่อน)
  entries.sort((a, b) => {
    // แปลงวันที่เป็น Date object เพื่อเปรียบเทียบ
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB - dateA;
  });

  // จัดกลุ่มการเปลี่ยนแปลงตามประเภท
  const groupChangesByType = (changes) => {
    const grouped = {};
    changes.forEach((change) => {
      if (!grouped[change.type]) {
        grouped[change.type] = [];
      }
      grouped[change.type].push(change);
    });
    return grouped;
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "400px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          p: 2,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "4px",
            "&:hover": {
              background: "#a1a1a1",
            },
          },
        }}
      >
        {entries.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              ไม่พบข้อมูล Changelog
            </Typography>
          </Box>
        ) : (
          entries.map((entry, idx) => {
            const groupedChanges = groupChangesByType(entry.changes);

            return (
              <Box
                key={idx}
                sx={{
                  mb: 3,
                  p: 3,
                  borderRadius: 3,
                  background: "#fff",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  border: "1px solid #e0e7ff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {/* Header ของแต่ละเวอร์ชัน */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                    pb: 2,
                    borderBottom: "2px solid #f0f4f8",
                  }}
                >
                  <Box
                    sx={{
                      background: "linear-gradient(45deg, #1976d2, #1565c0)",
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      mr: 2,
                      minWidth: "auto",
                    }}
                  >
                    v{entry.version}
                  </Box>
                  <Box
                    sx={{
                      background: "#4caf50",
                      color: "#fff",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1.5,
                      fontSize: "0.875rem",
                      fontWeight: "500",
                    }}
                  >
                    📅 {entry.date}
                  </Box>
                  <Box
                    sx={{
                      ml: "auto",
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        background: "#f5f5f5",
                        color: "#666",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                        fontSize: "0.75rem",
                        fontWeight: "500",
                      }}
                    >
                      📊 {entry.changes.length} การเปลี่ยนแปลง
                    </Box>
                    {Object.keys(groupedChanges).length > 0 && (
                      <Box
                        sx={{
                          background: "#e3f2fd",
                          color: "#1976d2",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1.5,
                          fontSize: "0.75rem",
                          fontWeight: "500",
                        }}
                      >
                        📂 {Object.keys(groupedChanges).length} ประเภท
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* รายละเอียดการเปลี่ยนแปลง */}
                <Box>
                  {Object.keys(groupedChanges).length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontStyle: "italic",
                        textAlign: "center",
                        py: 2,
                      }}
                    >
                      ไม่มีรายละเอียดการเปลี่ยนแปลง
                    </Typography>
                  ) : (
                    Object.entries(groupedChanges).map(([type, changes]) => (
                      <Box key={type} sx={{ mb: 2.5 }}>
                        {/* หัวข้อประเภทการเปลี่ยนแปลง */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              background: `${changes[0].color}20`,
                              color: changes[0].color,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              fontWeight: "bold",
                              fontSize: "0.9rem",
                              border: `1px solid ${changes[0].color}40`,
                            }}
                          >
                            <span style={{ marginRight: 6 }}>
                              {changes[0].icon}
                            </span>
                            {type} ({changes.length})
                          </Box>
                        </Box>

                        {/* รายการการเปลี่ยนแปลง */}
                        <ChangelogSection
                          changes={changes}
                          type={type}
                          color={changes[0].color}
                        />
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}

// Component สำหรับแสดงรายการการเปลี่ยนแปลงแต่ละประเภท
const ChangelogSection = ({ changes, type, color }) => {
  const [showAll, setShowAll] = useState(false);
  const ITEMS_PER_PAGE = 10;

  const displayedChanges = showAll ? changes : changes.slice(0, ITEMS_PER_PAGE);
  const hasMore = changes.length > ITEMS_PER_PAGE;

  return (
    <Box sx={{ pl: 2 }}>
      {displayedChanges.map((change, changeIdx) => (
        <Box
          key={changeIdx}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mb: 1,
            p: 1.5,
            borderRadius: 1.5,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            transition: "all 0.2s ease",
            "&:hover": {
              background: "#f1f5f9",
              borderColor: color,
            },
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: color,
              mt: 0.75,
              mr: 1.5,
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#334155",
                lineHeight: 1.6,
                fontSize: "0.9rem",
                mb: 0.5,
              }}
            >
              {change.description}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontSize: "0.75rem",
                fontFamily: "monospace",
                background: "#f1f5f9",
                px: 1,
                py: 0.25,
                borderRadius: 0.5,
              }}
            >
              #{change.commitHash}
            </Typography>
          </Box>
        </Box>
      ))}

      {hasMore && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowAll(!showAll)}
            startIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              color: color,
              borderColor: `${color}40`,
              backgroundColor: `${color}08`,
              "&:hover": {
                backgroundColor: `${color}16`,
                borderColor: color,
              },
            }}
          >
            {showAll
              ? `ซ่อนรายการ (${changes.length - ITEMS_PER_PAGE} รายการ)`
              : `ดูเพิ่มเติม (+${changes.length - ITEMS_PER_PAGE} รายการ)`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

ChangelogSection.propTypes = {
  changes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      commitHash: PropTypes.string.isRequired,
    })
  ).isRequired,
  type: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

const ChangelogModal = ({ open, onClose, changelog }) => {
  // ข้อมูลทดสอบในกรณีที่ changelog ว่างหรือไม่มีข้อมูล
  const fallbackChangelog = `# Changelog

## [2.3.5] - 2025-08-26

### Added
- ระบบการแสดง Changelog ใหม่แบบ Modal
- UI ที่ปรับปรุงใหม่สำหรับ Changelog
- ระบบ Auto Changelog อัตโนมัติ

### Changed  
- ปรับปรุงการแสดงผล Changelog เป็นแบบ Card
- เปลี่ยนแปลงรูปแบบ UI ให้ทันสมัย

### Fixed
- แก้ไขปัญหาการโหลด Changelog
- แก้ไขการแสดงผลใน Modal

## [2.3.4] - 2025-08-25

### Added
- สร้าง SelectData.js ใหม่ รองรับ static และ API data

### Fixed
- แก้ปัญหาไฟล์ว่างหลังการสร้างใหม่
`;

  // ใช้ข้อมูล fallback หาก changelog ว่างหรือไม่มี
  const displayChangelog =
    changelog && changelog.trim().length > 0 ? changelog : fallbackChangelog;

  // //console.log("ChangelogModal - changelog prop:", changelog);
  // //console.log("ChangelogModal - displayChangelog:", displayChangelog);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          background: "linear-gradient(90deg, #1976d2 0%, #388e3c 100%)",
          color: "#fff",
          fontWeight: "bold",
        }}
      >
        📚 Changelog
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          background: "linear-gradient(135deg, #e3f2fd 0%, #c8e6c9 100%)",
          minHeight: 300,
          maxHeight: 500,
          overflow: "hidden", // ปิด overflow ของ DialogContent
          p: 0, // ลบ padding เพื่อให้ scrollbar อยู่ใน Box
        }}
      >
        {renderMarkdown(displayChangelog)}
      </DialogContent>
      <DialogActions sx={{ background: "#f5f5f5" }}>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          sx={{ fontWeight: "bold" }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
ChangelogModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  changelog: PropTypes.string.isRequired,
};
export default ChangelogModal;
